import { h, Fragment } from 'preact'
import { useEffect, useState } from 'preact/compat'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import AddressField from './AddressField'
import AddressSelectBox from './AddressSelectBox'
import AddressFinder from './AddressFinder'
import LinkButton from '../Common/LinkButton'
import { isEmpty } from '../../helpers/common'
import {
  getAddressFields,
  getAddressItemsByKey,
  getAddressLine,
  getCountryCode,
  getCountryName,
} from '../../helpers/billingAddress'
import { useTranslations } from '../../hooks/useTranslations'
import { transactionDetailsSelector } from '../../redux/slice/transactionDetailsSlice'
import {billingAddressSelector, billingAddressUpdate} from '../../redux/slice/billingAddressSlice'
import AddressSelector from './AddressSelector'
import './styles.scss'

function BillingAddress({
  register,
  control,
  watch,
  errors,
  setValue,
  clearErrors,
  defaultBillingAddress = {},
}) {
  const l10n = useTranslations()
  const dispatch = useDispatch()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const initBillingAddress = useSelector(billingAddressSelector)

  const countryCodeWatcher = getCountryCode(watch('country'), l10n) || ''

  const countyStateText = {
    CA: l10n.t('Province/Territory'),
    defaultText: l10n.t('County/State'),
  }

  const { billingAddress, tenantId } = transactionDetails
  const validateCountryCode = () =>
    !!getCountryName(billingAddress?.country, l10n)

  const [isValidCountryCode, setIsValidCountryCode] = useState(
    validateCountryCode(),
  )
  const [isEditMode, setEditMode] = useState(false)
  const [foundAddress, setFoundAddress] = useState({})
  const [countyStateLabel, setCountyStateLabel] = useState(
    countyStateText.defaultText,
  )
  const handleAddressEnter = (
    addressLine1 = '',
    addressLine2 = '',
    locality = '',
    postalCode = '',
    state = '',
  ) => {
    setValue('addressLine1', addressLine1)
    setValue('addressLine2', addressLine2)
    setValue('city', locality)
    setValue('postalCode', postalCode)
    setValue('state', state)

    dispatch(
      billingAddressUpdate({
        addressLine1,
        addressLine2,
        city: locality,
        postalCode: postalCode,
        state,
      }),
    )

    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'billingAddressUpdate',
          message: {
            addressLine1,
            addressLine2,
            city: locality,
            postalCode,
            state,
          },
        },
      },
      '*',
    )
  }

  useEffect(() => {
    if (!validateCountryCode()) {
      setEditMode(true)
      setIsValidCountryCode(false)
    }

    dispatch(
      billingAddressUpdate({
        country: countryCodeWatcher,
      }),
    )

    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'billingAddressUpdate',
          message: {
            country: countryCodeWatcher,
          },
        },
      },
      '*',
    )

    setCountyStateLabel(
      countyStateText[countryCodeWatcher] || countyStateText.defaultText,
    )
  }, [countryCodeWatcher])

  const clearAddress = () => {
    handleAddressEnter()
    setFoundAddress([])
  }

  const fillDefaultBillingAddress = () => {
    handleAddressEnter()
    Object.keys(defaultBillingAddress).forEach(key => {
      if (key === 'country') {
        setValue(key, getCountryName(defaultBillingAddress[key], l10n))
      } else {
        setValue(key, defaultBillingAddress[key])
      }
    })
    dispatch(billingAddressUpdate(defaultBillingAddress))

    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'billingAddressUpdate',
          message: defaultBillingAddress,
        },
      },
      '*',
    )
  }

  const handleAddressSelect = address => {
    clearErrors()
    setValue('country', getCountryName(countryCodeWatcher, l10n))
    setFoundAddress(address)
  }

  useEffect(() => {
    if (!isEmpty(defaultBillingAddress)) {
      fillDefaultBillingAddress()
    } else {
      setEditMode(true)
    }
  }, [l10n])

  useEffect(() => {
    if (foundAddress?.length) {
      const { addressLine1 } = getAddressItemsByKey(
        foundAddress,
        'addressLine1',
      )
      const { addressLine2 } = getAddressItemsByKey(
        foundAddress,
        'addressLine2',
      )
      const { locality } = getAddressItemsByKey(foundAddress, 'locality')
      const { postalCode } = getAddressItemsByKey(foundAddress, 'postalCode')

      handleAddressEnter(addressLine1, addressLine2, locality, postalCode)
    }
  }, [foundAddress])

  const isCountyStateFieldAvailable =
    countryCodeWatcher === 'US' ||
    countryCodeWatcher === 'CA' ||
    countryCodeWatcher === 'GB'

  const isCountyStateRequiredField =
    countryCodeWatcher === 'US' || countryCodeWatcher === 'CA'

  const isAddressFinderAvailable = countryCodeWatcher === 'GB' // || countryWatcher === 'IE'
  const isInitCountryUSA = billingAddress?.country === 'US'
  const isInitCountryCA = billingAddress?.country === 'CA'

  let isAddressField2NotAvailable = false

  if (initBillingAddress?.country === 'BE' || initBillingAddress?.country === 'NL') {
    isAddressField2NotAvailable = !!initBillingAddress?.addressLine1
  }

  return (
    !transactionDetails.hideBillingAddress && (
      <div className="billing-address" data-testid="billing-address">
        <h3 className="billing-address__header medium nospace">
          {l10n.t('Billing Address')}
        </h3>
        {getAddressLine(defaultBillingAddress) &&
          !isInitCountryUSA &&
          !isInitCountryCA &&
          isValidCountryCode && (
            <>
              {!isEditMode && (
                <>
                  {getAddressLine(defaultBillingAddress)
                    .split(',')
                    .map((el, i) => {
                      return (
                        <p
                          className="billing-address__row"
                          data-testid={`billing-address__address-line-${i}`}
                        >
                          {el}
                        </p>
                      )
                    })}
                  <div
                    onClick={() => setEditMode(true)}
                    className="billing-address__edit-address"
                    data-testid="billing-address-finder"
                    tabIndex="0"
                    onKeyDown={e => {
                      if (e.code === 'Space' || e.key === 'Enter') {
                        setEditMode(true)
                      }
                    }}
                  >
                    {l10n.t('Change billing address')}
                  </div>
                </>
              )}
            </>
          )}

        {(isEditMode || isInitCountryUSA || isInitCountryCA) && (
          <>
            <AddressSelector
              setValue={setValue}
              errors={errors}
              control={control}
              preselectedCountry={tenantId === 'atcom' ? 'GB' : billingAddress?.country}
            />

            {isAddressFinderAvailable && (
              <AddressFinder
                onSelect={handleAddressSelect}
                countryCode={countryCodeWatcher}
              />
            )}
            {!isEmpty(foundAddress) && (
              <LinkButton
                id="select-address-manually"
                data-testid="clear-button"
                title={l10n.t('Enter Address Manually')}
                onClick={() => clearAddress()}
                onKeyDown={e => {
                  if (e.code === 'Space') {
                    clearAddress()
                  }
                }}
                tabIndex="0"
              />
            )}

            {getAddressFields(
              l10n,
              isCountyStateRequiredField,
              isCountyStateFieldAvailable,
              countyStateLabel,
              isAddressField2NotAvailable,
            ).map(
              field =>
                field.available &&
                (field.name === 'state' ? (
                  <AddressSelectBox
                    className="payment-form__field"
                    selectBoxClassName="country-state-select"
                    key={field.name}
                    register={register}
                    errors={errors}
                    field={field}
                    country={countryCodeWatcher}
                  />
                ) : (
                  <AddressField
                    key={field.name}
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    field={field}
                  />
                )),
            )}
          </>
        )}
      </div>
    )
  )
}

BillingAddress.propTypes = {
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  defaultBillingAddress: PropTypes.object,
}

export default BillingAddress
